import { FileText, Home, LifeBuoy, Mail, TrendingUp, User, UserPlus } from 'lucide-react';
import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';

import Logo from '@/components/common/Logo';
import cn from '@/utils/cn';
import { ROUTES } from '@/utils/constants';

interface NavItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
}

const NAV: NavItem[] = [
  { title: 'Home', url: ROUTES.SALES_RM_HOME, icon: Home },
  { title: 'Lead', url: ROUTES.SALES_RM_LEADS, icon: UserPlus },
  { title: 'Orders', url: ROUTES.SALES_RM_ORDERS, icon: TrendingUp },
  { title: 'Email Logs', url: ROUTES.SALES_RM_EMAIL_LOGS, icon: Mail },
  { title: 'T&C Docs', url: ROUTES.SALES_RM_TC_DOCUMENTS, icon: FileText },
  { title: 'Profile', url: ROUTES.SALES_RM_PROFILE, icon: User },
];

interface SalesSidebarProps {
  collapsed: boolean;
  // 'drawer' fills the off-canvas Sheet on phone/tablet: always expanded, full
  // width, and taller rows for touch. 'rail' is the desktop sidebar.
  variant?: 'rail' | 'drawer';
  onNavigate?: () => void;
}

export function SalesSidebar({ collapsed, variant = 'rail', onNavigate }: SalesSidebarProps) {
  const isDrawer = variant === 'drawer';

  return (
    <aside
      aria-label="Workspace navigation"
      className={cn(
        'flex shrink-0 flex-col border-sidebar-border bg-sidebar',
        isDrawer
          ? 'h-full w-full'
          : cn('border-r transition-[width] duration-200', collapsed ? 'w-16' : 'w-60'),
      )}
    >
      <div className="flex h-14 items-center border-b border-sidebar-border px-3">
        {/* onNavigate closes the drawer. The Logo is a Link too, so without this
            tapping it navigated and left the focus-trapped drawer mounted. */}
        <Logo to={ROUTES.SALES_RM_HOME} showWordmark={!collapsed} onClick={onNavigate} />
      </div>

      <nav aria-label="Workspace" className="flex-1 overflow-y-auto p-2">
        {!collapsed && (
          <p className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Workspace
          </p>
        )}
        <ul className="space-y-1">
          {NAV.map((item) => (
            <li key={item.title}>
              <NavLink
                to={item.url}
                title={item.title}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-lg px-2.5 text-sm font-semibold transition-colors',
                    // 44px rows in the drawer (touch); the desktop rail keeps its
                    // original density.
                    isDrawer ? 'min-h-11 py-2.5' : 'py-2',
                    collapsed && 'justify-center',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {/* Kept in the DOM when collapsed so the accessible name does
                    not depend on the `title` attribute alone. */}
                <span className={cn('truncate', collapsed && 'sr-only')}>{item.title}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {!collapsed && (
        <div className="border-t border-sidebar-border px-3 py-2 text-[10px] text-muted-foreground">
          {/* The footer's support mailto is `hidden sm:inline`, so below 640px
              the drawer is the only place it can be reached - and above 640px
              this copy hides so the two never show at the same viewport.
              onNavigate for the same reason the NavLinks have it: the mail
              client opens over a drawer that would otherwise stay mounted. */}
          {isDrawer && (
            <a
              href="mailto:support@advisorkhoj.com"
              onClick={onNavigate}
              className="mb-1 flex min-h-11 items-center gap-2.5 rounded-lg px-2.5 text-sm font-semibold text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground sm:hidden"
            >
              <LifeBuoy className="h-4 w-4 shrink-0" />
              <span className="truncate">support@advisorkhoj.com</span>
            </a>
          )}
          v0.1.0 · DEV
        </div>
      )}
    </aside>
  );
}
